if (!customElements.get('sticky-atc-panel')) {
  // How far (px) the shopper must scroll before the bar slides in on mobile.
  const MOBILE_REVEAL_OFFSET = 120;

  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');
      this.modernPicker = this.productSection.querySelector('variant-picker');

      // Cache elements
      this.priceElement = this.querySelector('.price');
      this.imageElement = this.querySelector('.sticky-atc-panel__media img');
      this.actionButton = this.querySelector('.js-sticky-atc-btn');
      this.closeButton = this.querySelector('.js-sticky-atc-close');

      // Translated strings
      this.strings = {
        buy: this.dataset.buyText || this.dataset.addToCartText || 'Buy',
        soldOut: this.dataset.soldOutText || 'Sold out'
      };
      this.anchor = this.dataset.anchor || 'variants';

      this.currentVariant = null;
      this.isAddingToCart = false;

      // Session dismissal — if the shopper closed the bar, keep it closed this session.
      this.dismissKey = `ha_sticky_atc_dismissed_${this.dataset.productId || 'x'}`;
      try {
        this.dismissed = sessionStorage.getItem(this.dismissKey) === '1';
      } catch (e) {
        this.dismissed = false;
      }
      if (this.dismissed) {
        this.classList.add('sticky-atc-panel--dismissed');
        return;
      }

      if ('IntersectionObserver' in window) {
        this.bindEvents();
      }

      this.initializeFromCurrentVariant();
      this.revealForViewport();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      if (this.scrollRevealHandler) window.removeEventListener('scroll', this.scrollRevealHandler);
    }

    bindEvents() {
      // Desktop: reveal the bar only once the product info block scrolls out of view.
      // Mobile: the bar is shown immediately (revealForViewport) and stays visible.
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });
      if (this.productInfo) observer.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
      // Mobile: reveal the bar only after the shopper starts scrolling (not immediately).
      this.scrollRevealHandler = this.revealOnScroll.bind(this);
      window.addEventListener('scroll', this.scrollRevealHandler, { passive: true });
      window.addEventListener('on:breakpoint-change', this.revealForViewport.bind(this));

      // Keep price / image / stock / button in sync with the real picker.
      this.productSection.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      if (this.actionButton) this.actionButton.addEventListener('click', this.handleButtonClick.bind(this));
      if (this.closeButton) this.closeButton.addEventListener('click', this.handleClose.bind(this));
    }

    /**
     * Shows the bar appropriately for the current viewport.
     * Mobile: visible immediately on page load (no scroll gating).
     * Desktop: revealed by the IntersectionObserver; just clear the anti-flash class.
     */
    revealForViewport() {
      if (this.dismissed) return;
      this.classList.remove('invisible');
      // Mobile: stay hidden until the shopper starts scrolling (revealOnScroll handles it,
      // incl. the case where the page is already scrolled past the offset on load).
      // Desktop: the IntersectionObserver reveals it once the product info scrolls away.
      if (!theme.mediaMatches.md) {
        this.revealOnScroll();
      }
    }

    /**
     * Mobile reveal: slide the bar in once the shopper has scrolled past the offset.
     * Reveals once, then stays visible (until dismissed / scrolled to the bottom).
     */
    revealOnScroll() {
      if (this.dismissed || this.revealed) return;
      if (theme.mediaMatches.md) return;
      if (window.scrollY > MOBILE_REVEAL_OFFSET) {
        this.revealed = true;
        this.classList.remove('sticky-atc-panel--out');
      }
    }

    /**
     * Initialize current variant + button state on load.
     */
    initializeFromCurrentVariant() {
      if (this.dataset.defaultVariantId) {
        this.currentVariant = {
          id: parseInt(this.dataset.defaultVariantId, 10),
          available: this.dataset.defaultVariantAvailable === 'true'
        };
        this.updateButton(this.currentVariant);
        return;
      }

      const picker = this.modernPicker;
      if (!picker) return;

      setTimeout(() => {
        if (picker.variant) {
          this.currentVariant = picker.variant;
        } else {
          try {
            const dataEl = picker.querySelector('script[type="application/json"]');
            const data = JSON.parse(dataEl.textContent);
            const variantId = new URLSearchParams(window.location.search).get('variant');
            this.currentVariant = variantId
              ? data.product.variants.find((v) => v.id === parseInt(variantId, 10))
              : data.product.variants.find((v) => v.available) || data.product.variants[0];
          } catch (e) {
            // ignore
          }
        }
        if (this.currentVariant) this.updateButton(this.currentVariant);
      }, 100);
    }

    handleVariantChange(evt) {
      const { variant, product } = evt.detail;
      this.currentVariant = variant;
      this.updatePrice(variant, product);
      this.updateImage(variant);
      this.updateButton(variant);
    }

    updateButton(variant) {
      if (!this.actionButton) return;
      const buyable = !!(variant && variant.available);
      this.actionButton.textContent = this.strings.buy;
      this.actionButton.dataset.mode = buyable ? 'add' : 'soldout';
      this.actionButton.disabled = !buyable;
      // Hide the button entirely when not buyable so it can't overlap the content;
      // the red stock status communicates availability instead.
      this.actionButton.style.display = buyable ? '' : 'none';
    }

    handleButtonClick(evt) {
      evt.preventDefault();
      if (this.actionButton.dataset.mode === 'add' && this.currentVariant) {
        this.addToCart();
      }
    }

    handleClose() {
      this.classList.add('sticky-atc-panel--dismissed');
      this.dismissed = true;
      try {
        sessionStorage.setItem(this.dismissKey, '1');
      } catch (e) {
        // ignore
      }
    }

    async addToCart() {
      if (this.isAddingToCart || !this.currentVariant) return;

      this.isAddingToCart = true;
      this.actionButton.classList.add('is-loading');
      this.actionButton.disabled = true;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      const formData = new FormData();
      formData.append('id', this.currentVariant.id);
      formData.append('quantity', 1);

      const cartDrawer = document.querySelector('cart-drawer');
      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();

        if (!response.ok) throw new Error(data.description || response.status);

        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble && data.sections) {
            cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
          }

          if (cartDrawer && theme.settings.afterAtc === 'drawer') {
            setTimeout(() => {
              cartDrawer.renderContents(data, true);
            }, 700);
          }
        }

        this.actionButton.classList.add('is-success');
      } catch (error) {
        console.error('Add to cart error:', error);
        if (cartDrawer) cartDrawer.refresh(true);
      } finally {
        setTimeout(() => {
          this.actionButton.classList.remove('is-loading', 'is-success');
          this.actionButton.disabled = this.currentVariant ? !this.currentVariant.available : true;
          this.isAddingToCart = false;
        }, 1400);
      }
    }

    updatePrice(variant, product) {
      if (!this.priceElement) return;

      const mainPrice = this.productSection.querySelector('.product-info__price > .price');
      if (!mainPrice) return;

      const mainPriceDefault = mainPrice.querySelector('.price__default');
      const stickyPriceDefault = this.priceElement.querySelector('.price__default');
      if (mainPriceDefault && stickyPriceDefault) {
        stickyPriceDefault.innerHTML = mainPriceDefault.innerHTML;
      }

      if (variant) {
        this.priceElement.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.priceElement.classList.toggle('price--sold-out', !variant.available);
      }
    }

    updateImage(variant) {
      if (!this.imageElement || !variant || !variant.featured_media) return;
      const newImageUrl = variant.featured_media.preview_image?.src;
      if (!newImageUrl) return;
      const sizedUrl = newImageUrl.replace(/(\.[^.]+)$/, '_112x$1');
      this.imageElement.src = sizedUrl;
      this.imageElement.srcset = `${sizedUrl} 1x, ${newImageUrl.replace(/(\.[^.]+)$/, '_224x$1')} 2x`;
    }

    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
